const STORAGE_KEY = 'exitIntentPopupShown';
const DISCOUNT_CODE = 'WACHT5';
const INACTIVITY_MS = 30000;

class ExitIntentPopup extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-exit-intent-overlay]');
    this.closeButton = this.querySelector('[data-exit-intent-close]');
    this.form = this.querySelector('#exit-intent-native-form');
    this.formState = this.querySelector('[data-exit-intent-form-state]');
    this.successState = this.querySelector('[data-exit-intent-success]');
    this.codeEl = this.querySelector('[data-exit-intent-code]');
    this.hiddenFrame = this.querySelector('[data-exit-intent-hidden-frame]');
    if (this.codeEl) this.codeEl.textContent = DISCOUNT_CODE;

    // Post the real customer form into a hidden same-origin iframe so the popup
    // doesn't navigate away. This is a genuine native form submission (like the
    // theme's own newsletter block), which Shopify's /contact endpoint accepts —
    // a plain fetch() POST gets rejected with "Missing CAPTCHA token".
    if (this.form && this.hiddenFrame) {
      this.form.target = this.hiddenFrame.name;
    }

    this.closeButton?.addEventListener('click', () => this.hide());
    this.overlay?.addEventListener('click', () => this.hide());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.hidden) this.hide();
    });
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.hiddenFrame?.addEventListener('load', () => this.onHiddenFrameLoad());

    this.isCartPage = this.dataset.template === 'cart';
    this.bindTriggers();
  }

  hasBeenShown() {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  }

  hasCheckedOut() {
    return sessionStorage.getItem('exitIntentOrderCompleted') === '1';
  }

  markShown() {
    sessionStorage.setItem(STORAGE_KEY, '1');
  }

  async cartHasItems() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return (cart.item_count || 0) > 0;
    } catch (error) {
      return false;
    }
  }

  async maybeShow() {
    if (this.hasBeenShown() || this.hasCheckedOut()) return;
    const hasItems = await this.cartHasItems();
    if (!hasItems) return;
    this.show();
  }

  show() {
    if (this.hasBeenShown()) return;
    this.hidden = false;
    this.markShown();
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => {
      this.querySelector('#exit-intent-email')?.focus();
    });
  }

  hide() {
    this.hidden = true;
    document.body.style.overflow = '';
  }

  bindTriggers() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches || 'ontouchstart' in window;

    if (!isMobile) {
      document.addEventListener('mouseleave', (event) => {
        if (event.clientY > 0) return;
        this.maybeShow();
      });
      return;
    }

    if (!this.isCartPage) return;

    // Back-button trigger: intercept the first back navigation attempt on the cart page.
    history.pushState({ exitIntentGuard: true }, '', location.href);
    window.addEventListener('popstate', () => {
      if (this.hasBeenShown()) return;
      history.pushState({ exitIntentGuard: true }, '', location.href);
      this.maybeShow();
    });

    // Inactivity trigger: 30s without interaction on the cart page.
    let inactivityTimer = window.setTimeout(() => this.maybeShow(), INACTIVITY_MS);
    const resetTimer = () => {
      window.clearTimeout(inactivityTimer);
      inactivityTimer = window.setTimeout(() => this.maybeShow(), INACTIVITY_MS);
    };
    ['scroll', 'touchstart', 'click'].forEach((eventName) => {
      document.addEventListener(eventName, resetTimer, { passive: true });
    });
  }

  onSubmit(event) {
    const emailInput = this.querySelector('#exit-intent-email');
    if (!emailInput?.value?.trim()) return;

    this.awaitingSubmit = true;
    const submitButton = this.form.querySelector('.exit-intent-popup__submit');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Even geduld...';
    }
    // No preventDefault: let the browser perform the real form POST into the
    // hidden iframe (see connectedCallback).
  }

  onHiddenFrameLoad() {
    if (!this.awaitingSubmit) return;
    this.awaitingSubmit = false;
    this.formState.hidden = true;
    this.successState.hidden = false;
  }
}

if (!customElements.get('exit-intent-popup')) {
  customElements.define('exit-intent-popup', ExitIntentPopup);
}

window.addEventListener('pageshow', () => {
  // If the buyer lands back on the store from the checkout thank-you flow, the cart is
  // empty again, so cartHasItems() already prevents a re-show. Belt-and-braces flag:
  if (document.referrer && /\/checkouts\//.test(document.referrer)) {
    sessionStorage.setItem('exitIntentOrderCompleted', '1');
  }
});
